import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { v4 } from "uuid";

import IcoClose from "@floji-internal/mui-icons/Close";

import { Strings } from "--root/strings/en";
import {
    TopicMutationActionAddOffnetMember,
    TopicMutationActionUpdateOffnetMember,


    TopicOffnetPersonLinkedIdentityContact
} from "--root/static_types/topic.types";

import { SwaggerMutateClient } from "--client/common/apis/rest";
import { useTopicWithRefresh } from "--client/common/data/PeopleHooks";
import { useCurrentWorkspace } from "--client/common/hooks/useLoggedInUser";

import { Button } from "--floponents-web/atoms/Button";
import { IconButton } from "--components/buttons/iconButton";
import { Surface, SurfaceActionBar, SurfaceHeader } from "--floponents-web/atoms/Surface";
import { useToastNotifications } from "--floponents-web/molecules/ToastNotification/ToastProvider";
import { Body1, Body3, Heading4, Heading5, Heading6 } from "--components/typography";

import { DisplayNameEditor } from "./components/DisplayNameEditor";
import { IdentityEditor } from "./components/IdentityEditor";
import { IdentityActions } from "./components/IdentityActions";
import { StyledTable, StyledTableRow } from "./components/Table";
import { SuggestedIdentities } from "./components/SuggestedIdentities";
import { CloudUserIdentity, TopicCloudUser, TopicCloudUserIdentity } from "--root/static_types/shared.types";
import { v458 } from "--client/common/utils/v458";

const StyledSection = styled.div`
    margin: ${props => props.theme.spacing(1, 1, 0, 1)};
`;
const StyledSectionTitle = styled.div`
    display: flex;
    margin: ${props => props.theme.spacing(0, 2, 1, 2)};
    flex-wrap: nowrap;
    align-items: center;
    > :first-child {
        flex: 1 0 auto;
    }
`;
const StyledSectionBody = styled(Surface).attrs({ noPadding: true, noRadius: true })`
    background: ${props => props.theme.palette.background};
    border-radius: ${props => props.theme.radius.small};
`;
const StyledSectionFooter = styled.div`
    margin: ${props => props.theme.spacing(1, 2, 1, 2)};
    align-items: center;
`;

export type OffnetIdentityType = CloudUserIdentity["type"];

export interface OffnetControlProps {
    streamId: string;
    offnetId?: string;
    autofillDisplayName?: string;
    autofillIdentity?: CloudUserIdentity;
    onClose: () => void;
    onUserAdded?: (user: TopicCloudUser) => void;
    onUserEdited?: (offnet_user: TopicCloudUser) => void;
}

export const OffnetControl = (props: OffnetControlProps) => {
    const { onClose, onUserAdded, onUserEdited } = props;

    const toast = useToastNotifications();
    const currentWorkspace = useCurrentWorkspace();
    const [stream, refreshStream] = useTopicWithRefresh(props.streamId);

    const offnetUser = stream?.type.name === "stream"
        ? stream.type.offnetPeople?.find(v => v.offnet_person_id === props.offnetId)
        : undefined;

    const [isUserModified, setIsUserModified] = useState<boolean>(false);
    const [errMessage, setErrMessage] = useState<string | undefined>();

    const [displayName, setDisplayName] = useState<string | undefined>();

    const [identities, setIdentities] = useState<TopicCloudUserIdentity[]>([]);
    const [pendingIdentity, setPendingIdentity] =
        useState<CloudUserIdentity | undefined>(props.autofillIdentity);

    useEffect(() => {
        if (props.autofillDisplayName) setDisplayName(props.autofillDisplayName);
        if (props.autofillIdentity) setPendingIdentity(props.autofillIdentity);
        if (!props.offnetId && !props.autofillIdentity) {
            setPendingIdentity({ type: "email", address: "" });
        }
    }, [props.autofillDisplayName, props.autofillIdentity, props.offnetId]);

    useEffect(() => {
        if (!offnetUser) return;
        setDisplayName(offnetUser.display_name);
        setIdentities(offnetUser.identities);
    }, [offnetUser]);

    const handleRemoveIdentity = useCallback((identity: CloudUserIdentity) => {
        setIdentities(v => v.filter(toFilter => toFilter !== identity));
        setIsUserModified(true);
    }, [setIdentities]);

    const handleAddIdentity = useCallback((identity: TopicCloudUserIdentity) => {
        if (identities.find(v => JSON.stringify(v) === JSON.stringify(identity))) {
            setErrMessage(Strings.offnetControl.duplicate_identity);
        } else {
            setIdentities(v => ([...v, identity]));
            setPendingIdentity(undefined);
            setErrMessage(undefined);
        }
        setIsUserModified(true);
    }, [identities, setIdentities, setPendingIdentity]);

    const handleLinkedIdentityAdded = useCallback((
        identity: TopicOffnetPersonLinkedIdentityContact
    ) => {
        setIdentities(existingIdentities => ([
            ...existingIdentities,
            { identity_id: v458(), type: identity.type, address: identity.value }
        ]));
        setIsUserModified(true);
    }, []);

    const [isEditingDisplayName, setIsEditingDisplayName] =
        useState(!!props.autofillDisplayName || !props.offnetId);

    const handleDisplayNameEditStart = useCallback(() => {
        setIsEditingDisplayName(true);
    }, []);
    const handleDisplayNameEditFinish = useCallback((newDisplayName: string) => {
        setDisplayName(existingDisplayName => {
            if (newDisplayName !== existingDisplayName) {
                setIsUserModified(true);
                return newDisplayName;
            }
            return existingDisplayName;
        });
        setIsEditingDisplayName(false);
    }, []);

    const handleSave = useCallback(async () => {
        try {
            let offnetMemberMutation:
                TopicMutationActionAddOffnetMember |
                TopicMutationActionUpdateOffnetMember;

            let newOffnetPersonId: string | undefined;
            if (!props.offnetId) {
                newOffnetPersonId = v4();
                offnetMemberMutation = {
                    type: "add-offnet-member",
                    offnetUser: {
                        integration_id: null,
                        identities,
                        display_name: displayName,
                        offnet_person_id: newOffnetPersonId
                    }
                }
            } else {
                offnetMemberMutation = {
                    type: "update-offnet-member",
                    offnetUser: {
                        integration_id: null,
                        identities,
                        display_name: displayName,
                        offnet_person_id: props.offnetId
                    }
                }
            }

            await SwaggerMutateClient.MutateTopic({
                actions: [{
                    type: "update",
                    topicId:
                        props.streamId,
                    actions: [offnetMemberMutation]
                }]
            });

            if (newOffnetPersonId && currentWorkspace) {
                onUserAdded?.({
                    integration_id: null,
                    identities,
                    display_name: displayName,
                    offnet_person_id: newOffnetPersonId,
                    created_by_userspace: currentWorkspace
                });
            } else if (props.offnetId && offnetUser) {
                onUserEdited?.({
                    integration_id: null,
                    identities,
                    display_name: displayName,
                    offnet_person_id: props.offnetId,
                    created_by_userspace: offnetUser.created_by_userspace
                });
            }
            onClose();
        } catch (err) {
            console.error(err);
            toast.addToast(
                <>
                    <Heading4>
                        {Strings.errors.genericTitle}
                    </Heading4>
                    <Body1>
                        {typeof err.message === "string" ? err.message.substring(0, 200) : undefined || Strings.errors.genericBody}
                    </Body1>
                </>,
                { appearance: "error" }
            );
        } finally {
            refreshStream();
        }
    }, [
        currentWorkspace, displayName, identities, offnetUser, onClose,
        onUserAdded, onUserEdited, props.offnetId, props.streamId, refreshStream,
        toast
    ]);

    if (!stream) return null;

    return (
        <>
            <SurfaceHeader>
                <Heading5>
                    {
                        props.offnetId
                            ? Strings.offnetControl.edit_offnet
                            : Strings.offnetControl.add_offnet
                    }
                </Heading5>
                <IconButton
                    icon={<IcoClose />}
                    color="greyInverted"
                    size="medium"
                    onClick={onClose}
                />
            </SurfaceHeader>

            <OffnetSection
                title={<Heading6>{Strings.offnetControl.basic_info}</Heading6>}
            >
                <StyledTableRow>
                    <DisplayNameEditor
                        offnetId={props.offnetId}
                        stream={stream}
                        identities={identities}
                        defaultName={displayName}
                        isEditing={isEditingDisplayName}
                        onEditStart={handleDisplayNameEditStart}
                        onEditFinish={handleDisplayNameEditFinish}
                    />
                </StyledTableRow>
            </OffnetSection>

            <OffnetSection
                title={<>
                    <Heading6>{Strings.offnetControl.contact_info}</Heading6>
                    <IdentityActions onAddIdentity={type => setPendingIdentity({ type, address: "" })} />
                </>}
                footer={errMessage
                    ? <Body3 color="danger-main">{errMessage}</Body3>
                    : !pendingIdentity && !identities.length
                        ? <Body3 color="warning-main">{Strings.offnetControl.contact_footer_invalid}</Body3>
                        : <Body3>{Strings.offnetControl.contact_footer}</Body3>}
                disableBorder={(!pendingIdentity && !identities.length)}
            >
                {identities.map(identity => <StyledTableRow key={identity.address}>
                    <IdentityEditor
                        initial={identity}
                        onRemove={() => handleRemoveIdentity(identity)}
                    />
                </StyledTableRow>)}

                {pendingIdentity &&
                    <StyledTableRow>
                        <IdentityEditor
                            initial={{ ...pendingIdentity, identity_id: v458() }}
                            onEditFinish={handleAddIdentity}
                            onRemove={() => {
                                setPendingIdentity(undefined); setErrMessage(undefined);
                            }}
                            isEditing
                        />
                    </StyledTableRow>}

                <SuggestedIdentities
                    stream={stream}
                    identities={identities}
                    onLinkedIdentityAdded={handleLinkedIdentityAdded}
                />
            </OffnetSection>

            <SurfaceActionBar>
                <Button color="grey" onClick={onClose}>{Strings.offnetControl.cancel}</Button>
                <Button
                    color="positive"
                    disabled={!isUserModified || isEditingDisplayName}
                    onClick={handleSave}
                >
                    {Strings.offnetControl.save}
                </Button>
            </SurfaceActionBar>
        </>
    );
};

interface OffnetSectionProps {
    title: React.ReactNode,
    footer?: React.ReactNode,
    disableBorder?: boolean
}

const OffnetSection: React.FC<OffnetSectionProps> = props => {
    const { title, footer, children, disableBorder } = props;
    return <StyledSection>
        <StyledSectionTitle>
            {title}
        </StyledSectionTitle>
        {!disableBorder && <StyledSectionBody>
            <StyledTable>
                {children}
            </StyledTable>
        </StyledSectionBody>}
        {footer && <StyledSectionFooter>
            {footer}
        </StyledSectionFooter>}
    </StyledSection>;
};
